import React, { lazy } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { RotateCw, TrendingUp, AlertCircle, Info } from "lucide-react";
import { useRevenueForecast } from "@/hooks/useRevenueForecast";
import { useInView } from "framer-motion";
import { format } from "date-fns";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/hover-card";

const LazyResponsiveContainer = lazy(() => import('recharts').then(mod => ({ default: mod.ResponsiveContainer })));
const LazyBarChart = lazy(() => import('recharts').then(mod => ({ default: mod.BarChart })));
const LazyCartesianGrid = lazy(() => import('recharts').then(mod => ({ default: mod.CartesianGrid })));
const LazyXAxis = lazy(() => import('recharts').then(mod => ({ default: mod.XAxis })));
const LazyYAxis = lazy(() => import('recharts').then(mod => ({ default: mod.YAxis })));
const LazyTooltip = lazy(() => import('recharts').then(mod => ({ default: mod.Tooltip })));
const LazyLegend = lazy(() => import('recharts').then(mod => ({ default: mod.Legend })));
const LazyBar = lazy(() => import('recharts').then(mod => ({ default: mod.Bar })));

const RevenueForecast: React.FC = () => {
  const { forecasts, loading, error, lastUpdated, refreshForecasts } = useRevenueForecast();
  const ref = React.useRef(null);
  const isInView = useInView(ref);
  
  const handleRefresh = () => {
    refreshForecasts();
    toast.success('Refreshing revenue forecasts...', {
      description: 'Analyzing your business data to provide updated revenue predictions.'
    });
  };
  
  // Format data for chart
  const chartData = forecasts.map(forecast => ({
    name: format(new Date(forecast.forecast_date), 'MMM yyyy'),
    Predicted: forecast.predicted_amount_cents / 100,
    confidence: forecast.confidence_level,
    dataPoints: forecast.data_points,
    factors: forecast.factors,
  }));
  
  const formatter = (value: number) => `$${value.toLocaleString()}`;
  
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white p-4 border rounded-lg shadow-md text-sm">
          <p className="font-bold mb-1">{label}</p>
          <p className="text-emerald-600 font-semibold">{`Projected Revenue: ${formatter(data.Predicted)}`}</p>
          <p className="text-gray-500 mt-1">{`Confidence: ${Math.round(data.confidence * 100)}%`}</p>
          {data.dataPoints && (
            <div className="mt-2 pt-2 border-t border-gray-100">
              <p className="text-xs text-gray-600 font-semibold">Based on:</p>
              <ul className="text-xs text-gray-500 mt-1">
                <li>{`${data.dataPoints.historical_months_analyzed} months of data`}</li>
                <li>{`${data.dataPoints.active_enrollments} active enrollments`}</li>
                <li>{`$${(data.dataPoints.upcoming_invoices_value / 100).toLocaleString()} in pending invoices`}</li>
              </ul>
            </div>
          )}
        </div>
      );
    }
    return null;
  };
  
  return (
    <Card className="border shadow-sm bg-white/50 backdrop-blur-sm">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <div>
          <CardTitle className="text-xl font-semibold flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-emerald-500" />
            Revenue Forecast
            <HoverCard>
              <HoverCardTrigger asChild>
                <Button variant="ghost" size="icon" className="h-6 w-6">
                  <Info className="h-4 w-4 text-muted-foreground" />
                </Button>
              </HoverCardTrigger>
              <HoverCardContent className="w-80">
                <div className="space-y-2">
                  <h4 className="text-sm font-semibold">About This Forecast</h4>
                  <p className="text-sm text-muted-foreground">
                    This AI-powered forecast uses your historical payment data, 
                    active enrollments, and upcoming invoices to predict future revenue.
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Predictions are more accurate for the near term and 
                    should be used as a planning guide rather than exact figures.
                  </p>
                </div>
              </HoverCardContent>
            </HoverCard>
          </CardTitle>
          <CardDescription>
            AI-powered 3-month revenue projection
          </CardDescription>
        </div>
        <div className="flex items-center gap-2">
          {lastUpdated && (
            <span className="text-xs text-muted-foreground">
              Updated {lastUpdated.toLocaleDateString()}
            </span>
          )}
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={handleRefresh} 
            disabled={loading}
            className="h-8 w-8"
          >
            <RotateCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </CardHeader>
      <CardContent ref={ref}>
        {loading ? (
          <div className="h-[300px] flex items-center justify-center">
            <div className="space-y-2 w-full">
              <Skeleton className="h-[250px] w-full" />
              <div className="flex justify-between">
                <Skeleton className="h-4 w-[100px]" />
                <Skeleton className="h-4 w-[100px]" />
                <Skeleton className="h-4 w-[100px]" />
              </div>
            </div>
          </div>
        ) : error ? (
          <div className="flex items-center gap-3 p-3 text-amber-800 bg-amber-50 rounded-lg">
            <AlertCircle className="h-5 w-5 flex-shrink-0" />
            <p className="text-sm">{error}</p>
          </div>
        ) : forecasts.length === 0 ? (
          <div className="h-[300px] flex flex-col items-center justify-center text-center p-4">
            <TrendingUp className="h-12 w-12 text-muted-foreground/50 mb-3" />
            <h3 className="text-lg font-medium">No forecast data available</h3>
            <p className="text-sm text-muted-foreground mb-4">
              We need more historical data to generate accurate revenue forecasts.
            </p>
            <Button onClick={handleRefresh}>Generate Forecast</Button>
          </div>
        ) : (
          <div className={`transition-opacity duration-700 ${isInView ? 'opacity-100' : 'opacity-0'}`}>
            <React.Suspense fallback={<div style={{height: 300}}>Loading chart…</div>}>
              <LazyResponsiveContainer width="100%" height={300}>
                <LazyBarChart
                  data={chartData}
                  margin={{
                    top: 20,
                    right: 30,
                    left: 20,
                    bottom: 5,
                  }}
                >
                  <LazyCartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <LazyXAxis dataKey="name" />
                  <LazyYAxis tickFormatter={formatter} />
                  <LazyTooltip content={<CustomTooltip />} />
                  <LazyLegend />
                  <LazyBar 
                    dataKey="Predicted" 
                    name="Projected Revenue" 
                    fill="#10b981" 
                    radius={[4, 4, 0, 0]} 
                  />
                </LazyBarChart>
              </LazyResponsiveContainer>
            </React.Suspense>
            
            <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
              {forecasts.map((forecast, index) => (
                <Card key={index} className={`border-l-4 ${index === 0 ? 'border-l-emerald-500' : index === 1 ? 'border-l-blue-500' : 'border-l-purple-500'}`}>
                  <CardContent className="p-4">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="text-sm font-medium">
                          {format(new Date(forecast.forecast_date), 'MMMM yyyy')}
                        </p>
                        <p className="text-2xl font-bold text-gray-900">
                          ${(forecast.predicted_amount_cents / 100).toLocaleString()}
                        </p>
                      </div>
                      <div className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100">
                        {Math.round(forecast.confidence_level * 100)}% confidence
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default RevenueForecast;
